import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import SEO from './SEO';
import {
  ArrowUpRight,
} from 'lucide-react';
import { services } from '../data/services';
import { useSound } from '../hooks/useSound';

/* ─── Main Services Component ─── */
const Services: React.FC = () => {
  const navigate = useNavigate();
  const { playClick } = useSound();

  const handleServiceClick = (slug: string) => {
    playClick();
    navigate(`/services/${slug}`);
  };

  return (
    <section id="services" className="bg-white pt-32 pb-16 md:py-32 xl:px-[50px] px-[20px] md:px-[30px]">
      <SEO 
        title="Our Services | Nuviosites | TDMA" 
        description="Explore our range of digital services including SEO optimization, web development, AI integration, and digital marketing strategies from Nuviosites | TDMA."
        url="https://tdma.one/services"
      />

      {/* ── MOBILE LAYOUT ─────────────────────────────── */}
      <div className="md:hidden">
        {/* Header */}
        <div className="mb-8 border-b border-gray-100 pb-6">
          <h1 className="text-[26px] font-bold font-heading text-[#1F1E1D] leading-tight mb-2 uppercase tracking-tight">
            Areas of Expertise
          </h1>
          <p className="text-[15px] text-[#3F4348] font-light italic">
            Delivering Precision, Performance &amp; Reliability
          </p>
        </div>

        {/* Compact card list */}
        <div className="flex flex-col gap-4">
          {services.map((service) => {
            const Icon = service.icon;
            return (
              <div
                key={service.id}
                onClick={() => handleServiceClick(service.slug)}
                className="flex items-center gap-4 px-5 py-5 bg-[#F9F9F9] rounded-2xl active:scale-[0.98] transition-all duration-200 cursor-pointer border border-transparent active:border-black/5"
              >
                {/* Icon pill */}
                <div className="shrink-0 w-12 h-12 rounded-xl bg-white flex items-center justify-center shadow-sm">
                  <Icon className="w-6 h-6 text-black stroke-[1.5]" />
                </div>
                {/* Text */}
                <div className="flex-1 min-w-0">
                  <h3 className="text-[16px] font-bold text-black mb-0.5 leading-snug">
                    {service.title}
                  </h3>
                  <div className="flex items-center gap-1.5 text-[12px] text-zinc-400 uppercase tracking-widest font-semibold">
                    <span>Learn more</span>
                    <ArrowUpRight className="w-3 h-3" />
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* CTA */}
        <div className="mt-12 text-center">
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 bg-black text-white text-[14px] font-semibold px-10 py-4 rounded-full hover:bg-zinc-800 transition-all duration-300 uppercase tracking-wider"
          >
            Start a Project
            <ArrowUpRight className="h-4 w-4" />
          </Link>
        </div>
      </div>

      {/* ── DESKTOP LAYOUT ────────────────────────────── */}
      <div className="hidden md:block">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 max-w-7xl mx-auto">

          {/* Title Card */}
          <div className="md:px-8 md:py-8 px-2 py-4 rounded-[20px] min-h-[300px] flex flex-col justify-start">
            <h1 className="text-[54px] font-light text-[#1F1E1D] leading-[0.9] mb-4 font-heading tracking-tighter uppercase">
              Areas of <br /><span className="font-bold">Expertise</span>
            </h1>
            <div className="w-12 h-1 bg-black mb-6" />
            <p className="text-xl text-[#3F4348] font-light italic leading-relaxed">
              Delivering Precision, Performance &amp; Reliability.
            </p>
          </div>

          {/* Service Cards */}
          {services.map((service, index) => {
            const Icon = service.icon;
            return (
              <div
                key={service.id}
                onClick={() => handleServiceClick(service.slug)}
                className={`
                  md:px-8 md:py-10 px-6 py-8 rounded-[30px] min-h-[300px] md:h-[352px]
                  flex flex-col justify-between group border border-[#EEE]
                  transition-all duration-500 hover:bg-black cursor-pointer
                  hover:shadow-[0_20px_50px_rgba(0,0,0,0.15)] hover:-translate-y-2
                  ${index === services.length - 1 ? 'col-span-1 sm:col-span-2 lg:col-span-1' : ''}
                `}
              >
                <div className="flex justify-between items-start">
                  <div className="w-14 h-14 rounded-2xl bg-[#F4F4F4] group-hover:bg-white/10 flex items-center justify-center transition-colors duration-300">
                    <Icon className="w-7 h-7 text-black group-hover:text-white transition-colors duration-300 stroke-[1.2]" />
                  </div>
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <ArrowUpRight className="w-6 h-6 text-white" />
                  </div>
                </div>
                
                <div className="w-full">
                  <h3 className="text-[24px] font-bold text-black group-hover:text-white mb-3 transition-colors duration-300 leading-tight">
                    {service.title}
                  </h3>
                  <p className="text-[15px] md:text-[16px] text-zinc-500 group-hover:text-zinc-400 transition-colors duration-300 leading-relaxed font-light line-clamp-3">
                    {service.description}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        {/* CTA */}
        <div className="mt-20 text-center">
          <Link
            to="/contact"
            className="inline-flex items-center gap-3 text-black hover:text-white bg-transparent hover:bg-black border border-black px-12 py-5 rounded-full transition-all duration-500 font-bold uppercase tracking-widest text-sm"
          >
            <span>Start a Project</span>
            <ArrowUpRight className="h-4 w-4" />
          </Link>
        </div>
      </div>

    </section>
  );
};

export default Services;